import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useWebinarT } from '@/hooks/useWebinarT';
import { Loader2, Pause, Play, Volume2, VolumeOff } from 'lucide-react';
import type { RootState } from '@/app/store';
import { useAppSelector } from '@/app/hooks';
import type { TriggerEventInput } from '@/features/live/types';
import { useChannel } from 'ably/react';
import { useChannelName } from '@/routes/AblyAuth';
import { cn } from '@/lib/utils';
import { fitBox, useAreaSize } from '@/lib/useFittedBox';

type AblyMessage = { name: string; data: unknown };

function normalizeChannelMessages(message: AblyMessage): AblyMessage[] {
  if (message.name === 'batch-events' && Array.isArray(message.data)) {
    return message.data.filter(
      (event): event is AblyMessage =>
        !!event &&
        typeof event === 'object' &&
        typeof (event as { name?: unknown }).name === 'string',
    );
  }
  return [message];
}

export type VideoLayoutState = {
  paused: boolean;
  muted: boolean;
  currentTime: number;
};

/** Lecture de l'état courant du lecteur de régie (la position avance sans notifier le parent). */
export type VideoPlayerStateControl = {
  getVideoLayoutState: () => VideoLayoutState;
};

export default function VideoLiveView({
  handleTriggerEvent,
  onVideoLayoutChange,
  videoPlayerStateRef,
  isSpeaker,
  isAdmin,
  fillStage,
}: {
  handleTriggerEvent?: (event: TriggerEventInput) => void;
  onVideoLayoutChange?: (state: VideoLayoutState) => void;
  videoPlayerStateRef?: RefObject<VideoPlayerStateControl | null>;
  isSpeaker?: boolean;
  isAdmin?: boolean;
  /** Aperçu WYSIWYG : remplit la boîte 16:9 du stage (pas d'aspect/marge propres, arrondi 20px). */
  fillStage?: boolean;
}) {
  const { t } = useWebinarT('webinars');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  // Aperçu WYSIWYG : boîte ajustée au ratio réel du fichier → la vidéo remplit (scale up compris).
  const [videoAspect, setVideoAspect] = useState<number | null>(null);
  const { ref: videoAreaRef, size: videoArea } = useAreaSize<HTMLDivElement>();
  const videoFittedBox = videoAspect != null ? fitBox(videoArea.w, videoArea.h, videoAspect) : null;

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const { webinarResources } = useAppSelector((state: RootState) => state.webinar);
  const { publicResource, publicResourceStates, adminResourceStates } = useAppSelector(
    (state: RootState) => state.live,
  );

  const isPresenter = isAdmin || isSpeaker;
  const resourceStates = isPresenter ? adminResourceStates : publicResourceStates;

  const liveVideoResource = resourceStates.find(
    (state) => state.resourceType === 'WEBINAR_RESOURCE' && state.live,
  );

  const liveVideo = useMemo(() => {
    return webinarResources.find((r) => r.id === liveVideoResource?.resourceId) || publicResource;
  }, [webinarResources, liveVideoResource, publicResource]);

  useEffect(() => {
    if (!isPresenter || !liveVideo?.resourceUrl || !videoRef.current) return;

    const video = videoRef.current;
    setIsLoading(true);

    video.crossOrigin = 'anonymous';

    const timestamp = new Date().getTime();
    const cleanUrl = liveVideo.resourceUrl.includes('?')
      ? `${liveVideo.resourceUrl}&t=${timestamp}`
      : `${liveVideo.resourceUrl}?t=${timestamp}`;

    video.src = cleanUrl;
    video.load();

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    video.addEventListener('loadeddata', handleCanPlay);
    return () => {
      video.removeEventListener('loadeddata', handleCanPlay);
    };
  }, [liveVideo?.id, isPresenter]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isPresenter) return;

    const handleLoadedMetadata = () => setDuration(video.duration);
    const handleTimeUpdate = () => setCurrentTime(video.currentTime);

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);

    if (video.readyState >= 1) setDuration(video.duration);
    setCurrentTime(video.currentTime);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [isPresenter, liveVideo?.id]);

  const getVideoLayoutState = (): VideoLayoutState => {
    const video = videoRef.current;
    return {
      paused: video?.paused ?? true,
      muted: video?.muted ?? false,
      currentTime: video?.currentTime ?? 0,
    };
  };

  useEffect(() => {
    const ref = videoPlayerStateRef;
    if (!ref) return;
    ref.current = {
      getVideoLayoutState: () => {
        const video = videoRef.current;
        return {
          paused: video?.paused ?? true,
          muted: video?.muted ?? false,
          currentTime: video?.currentTime ?? 0,
        };
      },
    };
    return () => {
      ref.current = null;
    };
  }, [videoPlayerStateRef]);

  const playVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    video.play();

    if (handleTriggerEvent) {
      handleTriggerEvent({
        eventType: 'VIDEO_PLAY',
        resourceType: 'WEBINAR_RESOURCE',
        resourceId: liveVideo?.id || 0,
        payloadJson: JSON.stringify({
          timeNow: new Date().getTime(),
        }),
      });
    }
    onVideoLayoutChange?.({ ...getVideoLayoutState(), paused: false });
  };

  const pauseVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    if (handleTriggerEvent) {
      handleTriggerEvent({
        eventType: 'VIDEO_PAUSE',
        resourceType: 'WEBINAR_RESOURCE',
        resourceId: liveVideo?.id || 0,
        payloadJson: JSON.stringify({
          timeNow: new Date().getTime(),
        }),
      });
    }
    onVideoLayoutChange?.({ ...getVideoLayoutState(), paused: true });
  };

  const toggleMute = async () => {
    const video = videoRef.current;
    if (!video) return;

    if (isMuted) {
      video.muted = false;
      setIsMuted(false);
      if (handleTriggerEvent) {
        handleTriggerEvent({
          eventType: 'VIDEO_UNMUTE',
          resourceType: 'WEBINAR_RESOURCE',
          resourceId: liveVideo?.id || 0,
          payloadJson: JSON.stringify({
            timeNow: new Date().getTime(),
          }),
        });
      }
      onVideoLayoutChange?.({ ...getVideoLayoutState(), muted: false });
    } else {
      video.muted = true;
      setIsMuted(true);
      if (handleTriggerEvent) {
        handleTriggerEvent({
          eventType: 'VIDEO_MUTE',
          resourceType: 'WEBINAR_RESOURCE',
          resourceId: liveVideo?.id || 0,
          payloadJson: JSON.stringify({
            timeNow: new Date().getTime(),
          }),
        });
      }
      onVideoLayoutChange?.({ ...getVideoLayoutState(), muted: true });
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = Math.max(0, Math.min((clickX / rect.width) * duration, duration));

    video.currentTime = newTime;
    setCurrentTime(newTime);

    if (handleTriggerEvent) {
      handleTriggerEvent({
        eventType: 'VIDEO_SEEK',
        resourceType: 'WEBINAR_RESOURCE',
        resourceId: liveVideo?.id || 0,
        payloadJson: JSON.stringify({
          currentTime: newTime,
          timeNow: new Date().getTime(),
        }),
      });
    }
    onVideoLayoutChange?.({ ...getVideoLayoutState(), currentTime: newTime });
  };

  const channelName = useChannelName();

  const activeVideoResourceIdRef = useRef<number | null>(null);
  activeVideoResourceIdRef.current = liveVideo?.id ?? null;

  useChannel(channelName ?? '', (message) => {
    if (!channelName || !isPresenter || !message) return;

    for (const current of normalizeChannelMessages(message as AblyMessage)) {
      if (current.name !== 'live-event') continue;

      const data = current.data as {
        resourceType?: string;
        resourceId?: unknown;
        eventType?: string;
        payloadJson?: unknown;
      };
      if (!data || typeof data !== 'object' || data.resourceType !== 'WEBINAR_RESOURCE') continue;

      const expectedId = activeVideoResourceIdRef.current;
      if (expectedId == null || Number(data.resourceId) !== Number(expectedId)) continue;

      const video = videoRef.current;
      if (!video) continue;

      if (data.eventType === 'VIDEO_PLAY') {
        void video.play();
      } else if (data.eventType === 'VIDEO_PAUSE') {
        video.pause();
      } else if (data.eventType === 'VIDEO_MUTE') {
        video.muted = true;
        setIsMuted(true);
      } else if (data.eventType === 'VIDEO_UNMUTE') {
        video.muted = false;
        setIsMuted(false);
      } else if (data.eventType === 'VIDEO_SEEK') {
        try {
          let payload: unknown = data.payloadJson;
          if (typeof payload === 'string') {
            payload = JSON.parse(payload);
          }
          const time =
            payload &&
            typeof payload === 'object' &&
            typeof (payload as { currentTime?: unknown }).currentTime === 'number'
              ? (payload as { currentTime: number }).currentTime
              : null;
          if (time !== null) {
            video.currentTime = time;
            setCurrentTime(time);
          }
        } catch (e) {
          console.error('Error parsing VIDEO_SEEK payload', e, data.payloadJson);
        }
      }
    }
  });

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;
  const canPublishVideoControls = Boolean(handleTriggerEvent);

  const videoElement = (
    <video
      ref={videoRef}
      className="w-full h-full object-contain"
      controls={false}
      muted={isMuted}
      playsInline
      onLoadedMetadata={(e) => {
        const w = e.currentTarget.videoWidth;
        const h = e.currentTarget.videoHeight;
        if (h > 0) {
          const aspect = w / h;
          setVideoAspect((previous) =>
            previous != null && Math.abs(previous - aspect) < 0.001 ? previous : aspect,
          );
        }
      }}
    />
  );

  return (
    <div
      ref={fillStage ? videoAreaRef : undefined}
      className={cn(
        'relative group',
        fillStage
          ? 'w-full h-full flex items-center justify-center'
          : 'w-full aspect-video rounded-2xl mt-8 h-full bg-black overflow-hidden',
      )}
    >
      {fillStage ? (
        <div
          className={`relative overflow-hidden rounded-[20px] ${videoFittedBox ? '' : 'h-full w-full'}`}
          style={
            videoFittedBox
              ? { width: videoFittedBox.width, height: videoFittedBox.height }
              : undefined
          }
        >
          {videoElement}
        </div>
      ) : (
        videoElement
      )}

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
          <Loader2 className="animate-spin text-white size-12" />
        </div>
      )}

      {!isSpeaker && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div
            className={cn(
              'w-full h-1 bg-white/30 rounded-full mb-3',
              canPublishVideoControls ? 'cursor-pointer' : 'cursor-default opacity-80',
            )}
            onClick={canPublishVideoControls ? handleProgressClick : undefined}
          >
            <div
              className="h-full bg-white rounded-full transition-all duration-150"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          {/* Contrôles */}
          <div className="absolute bottom-14 left-1/2 transform -translate-x-1/2 flex items-center justify-center gap-2 py-3 px-4 bg-white rounded-full shadow-lg">
            {videoRef.current?.paused ? (
              <button
                type="button"
                onClick={playVideo}
                disabled={!canPublishVideoControls}
                className="cursor-pointer"
                aria-label={t('adminLive.videos.pause')}
              >
                <Play className="size-6" />
              </button>
            ) : (
              <button
                type="button"
                onClick={pauseVideo}
                disabled={!canPublishVideoControls}
                className="cursor-pointer"
                aria-label={t('adminLive.videos.play')}
              >
                <Pause className="size-6" />
              </button>
            )}

            <button
              type="button"
              onClick={toggleMute}
              disabled={!canPublishVideoControls}
              className="cursor-pointer"
              aria-label={t('adminLive.videos.toggleMute')}
            >
              {isMuted ? <VolumeOff className="size-6" /> : <Volume2 className="size-6" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
